/**
 * Guided document path shared by the dashboard widgets. Each stage unlocks once
 * the previous one is complete:
 * Get Started → Tax Returns → Year-End Financials → YTD Financials → Closing Documents.
 * Requirements with several `options` are satisfied by uploading any one of them.
 */

// Generated by the platform rather than uploaded by the client.
const GENERATED_DOC_TYPES = ['application_pdf', 'pfs_pdf'];

export const countDocumentsByType = (documents = []) => {
  const counts = {};
  (Array.isArray(documents) ? documents : []).forEach((d) => {
    counts[d.documentType] = (counts[d.documentType] || 0) + 1;
  });
  return counts;
};

export const countUploadedDocuments = (documents = []) => (
  (Array.isArray(documents) ? documents : []).filter((d) => !GENERATED_DOC_TYPES.includes(d.documentType)).length
);

export const buildStages = (t, hasApplication, countOf) => {
  const year = new Date().getFullYear();
  const lastYear = year - 1;
  const k = (key, opts) => t(`dashboard.checklist.${key}`, opts);
  const upload = (key, docType, required = 1) => ({
    key, kind: 'upload', required,
    label: k(`items.${key}.label`, { year: lastYear }),
    description: k(`items.${key}.desc`, { year: key.startsWith('ytd') ? year : lastYear }),
    options: [{ docType }],
  });

  const stages = [
    {
      key: 'getStarted',
      requirements: [
        {
          key: 'application', kind: 'application', required: 1,
          label: k('items.application.label'),
          description: k('items.application.desc'),
        },
        {
          key: 'bankStatements', kind: 'bank', required: 3,
          label: k('items.bankStatements.label'),
          description: k('items.bankStatements.desc'),
          options: [{ docType: 'business_bank_statement' }],
        },
      ],
    },
    {
      key: 'taxReturns',
      requirements: [
        upload('businessTaxReturns', 'business_tax_return', 2),
        upload('personalTaxReturns', 'personal_tax_return', 2),
      ],
    },
    {
      key: 'priorFinancials',
      requirements: [
        upload('priorPL', 'profit_loss'),
        upload('priorBS', 'balance_sheet'),
      ],
    },
    {
      key: 'ytdFinancials',
      requirements: [
        upload('ytdPL', 'profit_loss_ytd'),
        upload('ytdBS', 'balance_sheet_ytd'),
      ],
    },
    {
      key: 'closing',
      requirements: [
        upload('driversLicense', 'drivers_license'),
        upload('voidedCheck', 'voided_check'),
        {
          ...upload('ownership', 'schedule_k1'),
          options: [
            { docType: 'schedule_k1', label: k('items.ownership.optK1') },
            { docType: 'articles_incorporation', label: k('items.ownership.optArticles') },
          ],
        },
        {
          ...upload('location', 'lease_agreement'),
          options: [
            { docType: 'lease_agreement', label: k('items.location.optLease') },
            { docType: 'mortgage_statement', label: k('items.location.optMortgage') },
          ],
        },
      ],
    },
  ];

  return stages.map((stage) => {
    const titleYear = stage.key === 'ytdFinancials' ? year : lastYear;
    const requirements = stage.requirements.map((req) => {
      if (req.kind === 'application') return { ...req, count: hasApplication ? 1 : 0, done: hasApplication };
      const count = req.options.reduce((sum, o) => sum + countOf(o.docType), 0);
      return { ...req, count, done: count >= req.required };
    });
    return {
      ...stage,
      title: k(`stages.${stage.key}.title`, { year: titleYear }),
      subtitle: k(`stages.${stage.key}.subtitle`, { year: titleYear }),
      intro: stage.key === 'getStarted' ? null : k(`stages.${stage.key}.intro`, { year: titleYear }),
      requirements,
      done: requirements.every((r) => r.done),
    };
  });
};

/** Which program-unlocking milestones the client has reached. */
export const getUnlockState = (stages, hasApplication, hasPFS) => {
  const req = (stageKey, reqKey) => stages
    .find((s) => s.key === stageKey)?.requirements.find((r) => r.key === reqKey)?.done || false;
  return {
    application: !!hasApplication,
    bankStatements: req('getStarted', 'bankStatements'),
    taxReturns: stages.find((s) => s.key === 'taxReturns')?.done || false,
    pfs: !!hasPFS,
  };
};
